/**
 * Backend telemetry capture API — the SigNoz/OpenTelemetry replacement for
 * `Sentry.captureMessage` / `Sentry.captureException`. Deliberately framework
 * agnostic (no node-only imports, no `.server.ts` suffix) so it can be
 * imported from pure logic modules like `webhook-decisions.ts` and from unit
 * tests without pulling in the Node SDK bootstrap.
 *
 * Call sites swap `Sentry.captureMessage(msg, { level, extra })` /
 * `Sentry.captureException(err, { extra })` for the functions here with the
 * same call shape — see `otel.server.ts` for the NodeSDK bootstrap that makes
 * these actually export somewhere, and AGENTS.md for the migration plan.
 *
 * Contract, matching Sentry's "no DSN configured = silent no-op": with no
 * global `LoggerProvider` registered (unit tests, OTEL disabled locally),
 * `@opentelemetry/api-logs` itself resolves `logs.getLogger()` to a no-op
 * logger — see `NoopLogger`/`ProxyLoggerProvider` in that package. Both
 * functions here are additionally wrapped in try/catch so a bug in this
 * module (or an exporter throwing) can never break the caller.
 */

import type { Span } from '@opentelemetry/api'
import { metrics, SpanStatusCode, trace } from '@opentelemetry/api'
import { logs, SeverityNumber } from '@opentelemetry/api-logs'
import {
  ATTR_EXCEPTION_MESSAGE,
  ATTR_EXCEPTION_STACKTRACE,
  ATTR_EXCEPTION_TYPE,
} from '@opentelemetry/semantic-conventions'

const LOGGER_NAME = 'masklabs'

export type CaptureLevel = 'warning' | 'error' | 'info'

const SEVERITY_BY_LEVEL: Record<
  CaptureLevel,
  { number: SeverityNumber; text: string }
> = {
  info: { number: SeverityNumber.INFO, text: 'INFO' },
  warning: { number: SeverityNumber.WARN, text: 'WARN' },
  error: { number: SeverityNumber.ERROR, text: 'ERROR' },
}

/** OTel log/span attributes must be scalars (or arrays of scalars) — coerce
 * anything else (objects, arrays, etc.) to a JSON string so `extra` payloads
 * of arbitrary shape never fail to attach. Exported for `business.server.ts`,
 * which shapes its attributes the same way. */
export function toAttributeScalar(value: unknown): string | number | boolean {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

/** Flatten a `extra` bag into `alert.ctx.<key>` attributes. */
function flattenExtra(
  extra: Record<string, unknown> | undefined,
): Record<string, string | number | boolean> {
  const attributes: Record<string, string | number | boolean> = {}
  if (!extra) return attributes
  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined) continue
    attributes[`alert.ctx.${key}`] = toAttributeScalar(value)
  }
  return attributes
}

/** Resolve the OTel logger, never throwing. Returns `undefined` if the
 * logs API isn't available for any reason (should not happen — even with no
 * global provider registered `getLogger` returns a working no-op logger —
 * but this keeps the "never throw" contract airtight). */
function safeGetLogger() {
  try {
    return logs.getLogger(LOGGER_NAME)
  } catch {
    return undefined
  }
}

/** Normalize an unknown throwable into a real `Error` so both the span
 * exception record and the log record have a stable message/stack shape. */
function toError(error: unknown): Error {
  if (error instanceof Error) return error
  const err = new Error(typeof error === 'string' ? error : String(error))
  return err
}

/**
 * Emit an alert-style log record (no active span/trace required). Mirrors
 * `Sentry.captureMessage(message, { level, extra })`.
 */
export function captureMessage(
  message: string,
  opts?: { level?: CaptureLevel; extra?: Record<string, unknown> },
): void {
  try {
    const logger = safeGetLogger()
    if (!logger) return
    const level = opts?.level ?? 'error'
    const severity = SEVERITY_BY_LEVEL[level]
    logger.emit({
      body: message,
      severityNumber: severity.number,
      severityText: severity.text,
      attributes: {
        'masklabs.alert': true,
        'alert.level': level,
        ...flattenExtra(opts?.extra),
      },
    })
  } catch {
    // Telemetry must never break the caller.
  }
}

/**
 * Record an exception on the active span (if any) and always also emit an
 * ERROR log record. Mirrors `Sentry.captureException(error, { extra })`.
 */
export function captureException(
  error: unknown,
  opts?: { extra?: Record<string, unknown> },
): void {
  const err = toError(error)

  try {
    const span = trace.getActiveSpan()
    if (span) {
      span.recordException(err)
      span.setStatus({ code: SpanStatusCode.ERROR })
    }
  } catch {
    // Telemetry must never break the caller.
  }

  try {
    const logger = safeGetLogger()
    if (!logger) return
    logger.emit({
      body: err.message,
      severityNumber: SeverityNumber.ERROR,
      severityText: 'ERROR',
      attributes: {
        [ATTR_EXCEPTION_TYPE]: err.name,
        [ATTR_EXCEPTION_MESSAGE]: err.message,
        [ATTR_EXCEPTION_STACKTRACE]: err.stack ?? '',
        'masklabs.error': true,
        ...flattenExtra(opts?.extra),
      },
    })
  } catch {
    // Telemetry must never break the caller.
  }
}

const TRACER_NAME = 'masklabs'

/**
 * Shared meter for custom business metrics. CAUTION: unlike the tracer API,
 * the metrics API has no proxy provider — an instrument created before
 * `otel.server.ts` registers the global MeterProvider is a no-op forever.
 * Route/handler modules load after the bootstrap (see `src/server.ts` import
 * order), so module-level `createCounter`/`createHistogram` there is safe;
 * don't create instruments in modules the bootstrap itself imports.
 */
export function getMeter() {
  return metrics.getMeter('masklabs')
}

/**
 * Run `fn` inside an active span named `name`. Records the exception + ERROR
 * status if `fn` throws (then rethrows — real errors must propagate), sets OK
 * otherwise, and always ends the span. No-op-safe: with no tracer provider
 * registered, `startActiveSpan` runs `fn` under a no-op span.
 */
export function withSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T> | T,
  attributes?: Record<string, string | number | boolean>,
): Promise<T> {
  const tracer = trace.getTracer(TRACER_NAME)
  return tracer.startActiveSpan(name, async (span) => {
    try {
      if (attributes) span.setAttributes(attributes)
      const result = await fn(span)
      span.setStatus({ code: SpanStatusCode.OK })
      return result
    } catch (err) {
      span.recordException(toError(err))
      span.setStatus({ code: SpanStatusCode.ERROR })
      throw err
    } finally {
      span.end()
    }
  })
}
